import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Box } from "@mui/material";
import FilterSection from "components/FilterSection/FilterSection";
import useAdmin from "app/routes/middleware/Admin/useAdmin";
import KeyOperatorChips from "components/FilterSection/KeyOperatorChips";
import GenericTable from "components/common/GenericTable";
import JumboCardQuick from "@jumbo/components/JumboCardQuick";
import ContentHeader from "components/ContentHeader";
import entityService from "app/services/entity-service";
import { ROUTES } from "app/utils/constants/routeConstants";
import useMetaTitle from "app/hooks/useMetaTitle";

const AdminEntityList = ({ entityConfig }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const isAdmin = useAdmin();

  // State from search params
  const rowsPerPage = Number(searchParams.get("size")) || 25;
  const page = Number(searchParams.get("page")) || 1;
  const searchQuery = searchParams.get("login.contains") || "";
  const initialKeysWithOperators = entityConfig.supportsFilter
    ? Array.from(searchParams.entries()).reduce((acc, [key, value]) => {
        const [field, operator] = key.split(".");
        const filterOperators =
          entityConfig.entityStructure.fields[field]?.filterOperators;
        if (operator && filterOperators && filterOperators.includes(operator)) {
          acc.push({ key: field, operator, searchTerm: value });
        }
        return acc;
      }, [])
    : [];

  // Component state
  const [search, setSearch] = useState(searchQuery);
  const [debouncedSearch, setDebouncedSearch] = useState(searchQuery);
  const [rows, setRows] = useState(rowsPerPage);
  const [currentPage, setCurrentPage] = useState(page);
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("id");
  const [keysWithOperators, setKeysWithOperators] = useState(
    initialKeysWithOperators
  );
  const [editingKey, setEditingKey] = useState(null);

  // Debounce search
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);
    return () => clearTimeout(handler);
  }, [search]);

  // Update search params
  const stableKeysWithOperators = useMemo(
    () => keysWithOperators,
    [keysWithOperators]
  );
  useEffect(() => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set("size", rows.toString());
      newParams.set("page", currentPage.toString());
      newParams.set("sort", `${orderBy},${order}`);

      if (entityConfig.supportsFilter && stableKeysWithOperators.length > 0) {
        stableKeysWithOperators.forEach(({ key, operator, searchTerm }) => {
          if (searchTerm) {
            newParams.set(`${key}.${operator}`, searchTerm);
          } else {
            newParams.delete(`${key}.${operator}`);
          }
        });
        newParams.delete("login.contains");
      } else if (debouncedSearch) {
        newParams.set("login.contains", debouncedSearch);
      } else {
        newParams.delete("login.contains");
      }
      return newParams;
    });
  }, [
    stableKeysWithOperators,
    rows,
    debouncedSearch,
    currentPage,
    order,
    orderBy,
    setSearchParams,
    entityConfig.supportsFilter,
    entityConfig.entityStructure,
  ]);

  // Handlers
  const handleKeyChange = (newKeys) => {
    if (!entityConfig.supportsFilter) return;
    const currentKeys = keysWithOperators.map(({ key }) => key);
    const addedKeys = newKeys
      .filter((key) => !currentKeys.includes(key))
      .map((key) => ({
        key,
        operator: entityConfig.entityStructure.fields[key].filterOperators[0],
        searchTerm: "",
      }));
    const updatedKeysWithOperators = [
      ...keysWithOperators.filter(({ key }) => newKeys.includes(key)),
      ...addedKeys,
    ];
    setKeysWithOperators(updatedKeysWithOperators);
    setCurrentPage(1);
    if (!editingKey && addedKeys.length > 0) {
      setEditingKey(addedKeys[0].key);
      setSearch("");
    }
  };

  const handleSearchChange = (newSearch) => {
    setSearch(newSearch);
    if (entityConfig.supportsFilter && editingKey) {
      setKeysWithOperators((prev) =>
        prev.map((item) =>
          item.key === editingKey ? { ...item, searchTerm: newSearch } : item
        )
      );
    }
    setCurrentPage(1);
  };

  const handleReset = () => {
    setSearch("");
    setDebouncedSearch("");
    setRows(25);
    setCurrentPage(1);
    setKeysWithOperators([]);
    setEditingKey(null);
    setOrder("asc");
    setOrderBy("id");
    setSearchParams(new URLSearchParams());
  };

  const handleDeleteKey = (keyToDelete) => {
    if (!entityConfig.supportsFilter) return;
    setKeysWithOperators((prev) =>
      prev.filter(({ key }) => key !== keyToDelete)
    );
    setCurrentPage(1);
    if (editingKey === keyToDelete) {
      setEditingKey(null);
      setSearch("");
    }
  };

  const handleOperatorChange = (key, newOperator) => {
    if (!entityConfig.supportsFilter) return;
    setKeysWithOperators((prev) =>
      prev.map((item) =>
        item.key === key ? { ...item, operator: newOperator } : item
      )
    );
    setCurrentPage(1);
  };

  const handleEditFilter = (key, searchTerm) => {
    if (!entityConfig.supportsFilter) return;
    setEditingKey(key);
    setSearch(searchTerm || "");
    setCurrentPage(1);
  };

  const handleAdd = useCallback(() => {
    if (entityConfig.uiPaths.add?.path) {
      navigate(entityConfig.uiPaths.add.path);
    }
  }, [entityConfig.uiPaths.add, navigate]);

  const handleBack = useCallback(() => {
    navigate(-1);
    setTimeout(() => {
      if (window.location.pathname) {
        navigate(ROUTES.USER_HOME);
      }
    }, 100);
  }, [navigate]);

  const handleAction = useCallback(
    (action) => {
      switch (action.id) {
        case "back":
          handleBack();
          break;
        case "add":
          handleAdd();
          break;
        default:
          console.log("Unknown action:", action);
      }
    },
    [handleAdd, handleBack]
  );

  // Prepare additional fetch parameters (e.g., filters)
  const additionalFetchParams = useMemo(() => {
    const params = {};
    if (entityConfig.supportsFilter && stableKeysWithOperators.length > 0) {
      stableKeysWithOperators.forEach(({ key, operator, searchTerm }) => {
        if (searchTerm) {
          params[`${key}.${operator}`] = searchTerm;
        }
      });
    } else if (debouncedSearch) {
      params["login.contains"] = debouncedSearch;
    }
    return params;
  }, [stableKeysWithOperators, entityConfig.supportsFilter, debouncedSearch]);

  useMetaTitle({
    title: entityConfig.sectionTitle,
  });

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        height: "100%",
        overflow: "visible",
      }}
    >
      <JumboCardQuick
        title={
          <ContentHeader
            entityConfig={entityConfig}
            isAdmin={isAdmin}
            onAction={handleAction}
            allowedActions={isAdmin ? ["add", "back"] : ["back"]}
            showLeftTabs={true}
            actionLabels={{ add: `Add new ${entityConfig.sectionTitle}` }}
          />
        }
        headerSx={{
          borderBottom: 1,
          borderColor: "divider",
          "& .MuiCardHeader-action": { my: -0.75 },
        }}
        wrapperSx={{ p: 0 }}
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          "& .MuiCardContent-root:last-child": { paddingBottom: 0 },
          "& .MuiCardContent-root": {
            display: "flex",
            flexDirection: "column",
            height: "100%",
            justifyContent: "top",
          },
        }}
      >
        <FilterSection
          isAdmin={isAdmin}
          isAdminContext={true}
          tags={[]}
          selectedTags={[]}
          keysWithOperators={keysWithOperators}
          search={search}
          rows={rows}
          onTagChange={() => {}}
          onKeyChange={handleKeyChange}
          onSearchChange={handleSearchChange}
          onReset={handleReset}
          onRowsPerPageChange={(e) => {
            setRows(+e.target.value);
            setCurrentPage(1);
          }}
          addRoute={entityConfig.uiPaths.add}
          navigate={navigate}
          editingKey={editingKey}
          setEditingKey={setEditingKey}
          entityConfig={entityConfig}
        />
        {entityConfig.supportsFilter && (
          <KeyOperatorChips
            entityConfig={entityConfig}
            keysWithOperators={keysWithOperators}
            editingKey={editingKey}
            onDeleteKey={handleDeleteKey}
            onOperatorChange={handleOperatorChange}
            onEditFilter={handleEditFilter}
          />
        )}
        <GenericTable
          entityConfig={entityConfig}
          fetchService={entityService.fetchList}
          updateService={entityService.update}
          onBooleanToggleMessage={(row, fieldId, newValue) =>
            `User ${row.login}'s account has been ${
              newValue ? "activated" : "deactivated"
            }`
          }
          page={currentPage}
          pageSize={rows}
          sortBy={orderBy}
          sortOrder={order}
          onPageChange={setCurrentPage}
          onPageSizeChange={setRows}
          onSortChange={(newSortBy, newSortOrder) => {
            setOrderBy(newSortBy);
            setOrder(newSortOrder);
          }}
          additionalFetchParams={additionalFetchParams}
        />
      </JumboCardQuick>
    </Box>
  );
};

export default AdminEntityList;
